import gql from "@/api/gql.js"
import config from "@/config/"
import { query, mutation } from "./queries.js"
import resolveApiErrors from "@/api/resolveApiErrors"
import store from "@/store/"

const muangThaiPolicyTransform = function(policy) {
	const benefitOptionsMapping = {
		top_hit_package: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.late_stage",
			option_2: "benefit_options_value.late_stage_ci"
		},
		pa_go_premium_plan: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.plan_1",
			option_2: "benefit_options_value.plan_2",
			option_3: "benefit_options_value.plan_3"
		},
		top_up_premium_plan: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.plan_1",
			option_2: "benefit_options_value.plan_2",
			option_3: "benefit_options_value.plan_3"
		},
		group_cancer: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.late_stage",
			option_2: "benefit_options_value.late_stage_ci"
		},
		group_cadio: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.late_stage",
			option_2: "benefit_options_value.late_stage_ci"
		},
		group_neurological: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.late_stage",
			option_2: "benefit_options_value.late_stage_ci"
		},
		group_others: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.late_stage",
			option_2: "benefit_options_value.late_stage_ci"
		},
		group_major_organ_failure: {
			option_0: "benefit_options_value.not_selected",
			option_1: "benefit_options_value.late_stage",
			option_2: "benefit_options_value.late_stage_ci"
		}
	}
	const factMappingOrder = {
		"Buy for self": 10,
		"Id expiry date": 9,
		Weight: 8,
		Height: 7,
		"Health question 1": 6,
		"Health question 2": 5,
		"Health question 3": 4,
		"Health question 4": 3,
		"fatca question 1": 2,
		"fatca question 2": 1
	}
	const byOrder = (a, b) =>
		(factMappingOrder[b.type] || 0) - (factMappingOrder[a.type] || 0)
	policy.benefitOptions.forEach(option => {
		if (option.benefitTypeId && option.value.stringValue) {
			option.formattedValue = benefitOptionsMapping[option.benefitTypeId]
				? benefitOptionsMapping[option.benefitTypeId][option.value.stringValue]
				: null
		}
	})
	policy.contractInsured.forEach(i => {
		i.facts = i.facts.sort(byOrder)
	})
	return policy
}

const transformPolicyPerTenant = function(policy) {
	const policyCloned = JSON.parse(JSON.stringify(policy))
	const tenantsTransform = {
		muangThai_uat: muangThaiPolicyTransform
	}
	if (tenantsTransform[store.state.tenantId]) {
		return tenantsTransform[store.state.tenantId](policyCloned)
	}
	return policy
}

export default {
	async templates(variables) {
		const res = await gql({
			query: query.templates,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "templates"
		})
	},

	async policies(variables) {
		const res = await gql({
			query: query.policies,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async policy(variables) {
		let res = await gql({
			query: query.policy,
			variables
		})

		if (res.data.policies.list.length > 0) {
			res.data.policies.list[0] = transformPolicyPerTenant(
				res.data.policies.list[0]
			)
		}

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async benefitSummary(variables) {
		const res = await gql({
			query: query.benefitSummary,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async cancelPolicy(variables) {
		const res = await gql({
			query: mutation.cancelPolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "cancelPolicy"
		})
	},

	async sendNotification(variables) {
		const res = await gql({
			query: mutation.sendNotification,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "sendNotification"
		})
	},

	async removeCommissionRuleFromPolicy(variables) {
		const res = await gql({
			query: mutation.removeCommissionRuleFromPolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "removeCommission"
		})
	},

	async updatePolicyCommissionRule(variables) {
		const res = await gql({
			query: mutation.updatePolicyCommissionRule,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "updateCommission"
		})
	},

	async addCommissionRuleToPolicy(variables) {
		const res = await gql({
			query: mutation.addCommissionRuleToPolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "addCommission"
		})
	},

	async commissionSplitAndSummary(variables) {
		const res = await gql({
			query: query.commissionSplitAndSummary,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async policyLogs(variables) {
		const res = await gql({
			query: query.policyLogs,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async policyTransactions(variables) {
		const res = await gql({
			query: query.policyTransactions,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async policyPayments({ policyId, tenantId }) {
		// query the Payments service (V2) for this policy's payments.
		// Uses the shared fetcher's url override to hit ENDPOINT_V2; tenant header
		// scopes the multitenant Payments service (the token also carries the tenant).
		return await gql({
			query: query.policyPayments,
			variables: { where: { policyId: { eq: policyId } } },
			url: config.ENDPOINT_V2,
			headers: { tenant: tenantId }
		})
	},

	async policyClaims(variables) {
		const res = await gql({
			query: query.policyClaims,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithList",
			res,
			resKey: "policies"
		})
	},

	async initializeTransaction(variables) {
		const res = await gql({
			query: mutation.initializeTransaction,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithoutStatus",
			res,
			resKey: "initializeTransaction"
		})
	},

	async addPaymentInfo(variables) {
		const res = await gql({
			query: mutation.addPaymentInfo,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "addPaymentInfo"
		})
	},

	async updatePaymentInfo(variables) {
		const res = await gql({
			query: mutation.updatePaymentInfo,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "updatePaymentInfo"
		})
	},

	async removePaymentInfo(variables) {
		const res = await gql({
			query: mutation.removePaymentInfo,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "removePaymentInfo"
		})
	},

	async addTransaction(variables) {
		const res = await gql({
			query: mutation.addTransaction,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "createTransaction"
		})
	},

	async updateTransaction(variables) {
		const res = await gql({
			query: mutation.updateTransaction,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "updateTransaction"
		})
	},

	async deletePolicy(variables) {
		const res = await gql({
			query: mutation.deletePolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "status"
		})
	},

	async updatePolicy(variables) {
		const res = await gql({
			query: mutation.updatePolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "updatePolicy"
		})
	},

	async addEndorsement(variables) {
		const res = await gql({
			query: mutation.addEndorsement,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "addEndorsement"
		})
	},

	async issuePolicy(variables) {
		const res = await gql({
			query: mutation.issuePolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "mutationWithStatus",
			res,
			resKey: "issuePolicy"
		})
	},

	async validatePolicy(variables) {
		const res = await gql({
			query: query.validatePolicy,
			variables
		})

		return resolveApiErrors({
			resolutionType: "queryWithoutList",
			res,
			resKey: "validatePolicy_2"
		})
	}
}
